import React, { useState, useEffect } from 'react';
import { User, Calendar, Clock, AlertCircle, Check, Repeat } from 'lucide-react';
import { useApi } from '../../../../hooks/useApi';
import { TIME_SLOTS } from '../constants';

const BlockCounselorTimeSlot = () => {
  const { get, post, del} = useApi();
  const [counselors, setCounselors] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockEntireDay, setBlockEntireDay] = useState(false);
  const [blockingReason, setBlockingReason] = useState('unavailable'); // 'unavailable' or 'non-working'
  const [isRecurring, setIsRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  const [recurringBlocks, setRecurringBlocks] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);

  // Helper to get weekday name
  const getWeekdayName = (date) => {
    return new Date(date).toLocaleDateString('en-US', { weekday: 'long' });
  };

  // Helper to get weekday number (0-6)
  const getWeekdayNumber = (date) => {
    return new Date(date).getDay();
  };

  // Fetch counselors on mount
  useEffect(() => {
    const fetchCounselors = async () => {
      try {
        const response = await get('/users/counselors');
        if (response.data?.status === 'success') {
          setCounselors(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedCounselor(response.data.data[0]._id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch counselors:', error);
      }
    };

    fetchCounselors();
  }, [get]);

  // Fetch blocked slots when counselor or date changes
  useEffect(() => {
    const fetchBlockedSlots = async () => {
      if (!selectedCounselor || !selectedDate) return;
      
      setLoadingBlocked(true);
      try {
        // Fetch regular blocks for the specific date
        const response = await get('/appointments/blocked-slots', {
          params: {
            counselorId: selectedCounselor,
            date: selectedDate
          }
        });
        
        if (response.data?.status === 'success') {
          // Separate blocked slots and blocked days
          const slots = response.data.data.filter(block => block.type === 'blocked');
          const days = response.data.data.filter(block => block.type === 'blocked-day');
          
          setBlockedSlots(slots);
          setBlockedDays(days);
        }

        // Fetch recurring weekly blocks for this counselor
        const recurringResponse = await get('/appointments/recurring-blocks', {
          params: {
            counselorId: selectedCounselor
          }
        });

        if (recurringResponse.data?.status === 'success') {
          setRecurringBlocks(recurringResponse.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch blocked slots:', error);
      } finally {
        setLoadingBlocked(false);
      }
    };
    
    fetchBlockedSlots();
  }, [selectedCounselor, selectedDate, get]);

  const handleBlockTimeSlot = async () => {
    if (!selectedCounselor || !selectedDate) {
      setMessage({ type: 'error', text: 'Please select a counselor and date' });
      return;
    }
    
    if (!blockEntireDay && !selectedTimeSlot) {
      setMessage({ type: 'error', text: 'Please select a time slot' });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    const reason = blockEntireDay 
      ? `${blockingReason === 'non-working' ? 'Non-working day' : 'Unavailable'}${blockReason ? ': ' + blockReason : ''}`
      : blockReason;
    
    try {
      const response = await post('/appointments/block-slot', {
        counselorId: selectedCounselor,
        date: selectedDate,
        timeSlot: blockEntireDay ? null : selectedTimeSlot,
        reason,
        blockEntireDay,
        recurrence: (blockEntireDay && isRecurring && blockingReason === 'non-working') ? 'weekly' : null
      });
      
      if (response.data?.status === 'success') {
        setMessage({ 
          type: 'success', 
          text: (blockEntireDay && isRecurring) 
            ? `Every ${getWeekdayName(selectedDate)} blocked permanently` 
            : blockEntireDay 
              ? 'Entire day blocked successfully' 
              : 'Time slot blocked successfully' 
        });
        
        // Show warning if there were existing appointments
        if (response.data.data.existingAppointments && response.data.data.existingAppointments.length > 0) {
          setMessage({
            type: 'warning',
            text: `${blockEntireDay ? 'Day' : 'Time slot'} blocked, but there ${
              response.data.data.existingAppointments.length === 1 ? 'is' : 'are'
            } ${response.data.data.existingAppointments.length} existing appointment(s) that need to be rescheduled`
          });
        }
        
        // Reset form
        if (!blockEntireDay) {
          setSelectedTimeSlot('');
        }
        setBlockReason('');
        setIsRecurring(false);
        
        // Refresh blocked slots
        const updatedBlocksResponse = await get('/appointments/blocked-slots', {
          params: {
            counselorId: selectedCounselor,
            date: selectedDate
          }
        });
        
        if (updatedBlocksResponse.data?.status === 'success') {
          const slots = updatedBlocksResponse.data.data.filter(block => block.type === 'blocked');
          const days = updatedBlocksResponse.data.data.filter(block => block.type === 'blocked-day');
          
          setBlockedSlots(slots);
          setBlockedDays(days);
        }

        // Refresh recurring blocks
        const recurringResponse = await get('/appointments/recurring-blocks', {
          params: {
            counselorId: selectedCounselor
          }
        });

        if (recurringResponse.data?.status === 'success') {
          setRecurringBlocks(recurringResponse.data.data);
        }
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to block time slot' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockTimeSlot = async (blockId, isRecurring = false) => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const endpoint = isRecurring 
        ? `/appointments/recurring-block/${blockId}` 
        : `/appointments/block-slot/${blockId}`;
        
      const response = await del(endpoint);
      
      if (response.data?.status === 'success') {
        setMessage({ 
          type: 'success', 
          text: isRecurring 
            ? 'Weekly recurring block removed successfully' 
            : 'Time slot unblocked successfully' 
        });
        
        // Remove the unblocked slot from state
        if (isRecurring) {
          setRecurringBlocks(recurringBlocks.filter(block => block._id !== blockId));
        } else {
          setBlockedSlots(blockedSlots.filter(block => block._id !== blockId));
          setBlockedDays(blockedDays.filter(block => block._id !== blockId));
        }
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to unblock time slot' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a time slot is already blocked
  const isTimeSlotBlocked = (timeSlot) => {
    return blockedSlots.some(block => 
      timeSlot >= block.startTime && timeSlot < block.endTime
    );
  };

  // Helper to check if the entire day is blocked
  const isDayBlocked = () => {
    return blockedDays.length > 0;
  };

  // Helper to check if this weekday is already in a recurring block
  const isWeekdayInRecurringBlock = () => {
    const weekday = getWeekdayNumber(selectedDate);
    return recurringBlocks.some(block => block.weekDay === weekday);
  };

  const getSelectedCounselorName = () => {
    const counselor = counselors.find(c => c._id === selectedCounselor);
    return counselor ? counselor.fullName : '';
  };

  // Check if current day is already in a recurring block
  const currentDayRecurringBlock = recurringBlocks.find(
    block => block.weekDay === getWeekdayNumber(selectedDate)
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4">Block Counselor Time Slots</h2>
      
      {message.text && (
        <div className={`mb-4 p-3 rounded ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 
          message.type === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
          'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}
      
      {currentDayRecurringBlock && (
        <div className="mb-4 p-3 rounded bg-blue-100 text-blue-800">
          <div className="flex items-center">
            <Repeat className="h-4 w-4 mr-2" />
            Note: {getWeekdayName(selectedDate)} is already set as a recurring non-working day for this counselor.
          </div>
          <div className="mt-1 text-sm">{currentDayRecurringBlock.description}</div>
        </div>
      )}
      
      <div className="space-y-4">
        {/* Counselor Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Select Counselor</label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-uzima-maroon-400 focus:ring-uzima-maroon-400"
            value={selectedCounselor}
            onChange={(e) => setSelectedCounselor(e.target.value)}
          >
            {counselors.map((counselor) => (
              <option key={counselor._id} value={counselor._id}>
                {counselor.fullName}
              </option>
            ))}
          </select>
        </div>
        
        {/* Date Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Select Date</label>
          <input
            type="date"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-uzima-maroon-400 focus:ring-uzima-maroon-400"
            value={selectedDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
        
        {/* Block Entire Day Option */}
        <div className="flex items-center mt-4">
          <input
            type="checkbox"
            id="blockEntireDay"
            checked={blockEntireDay}
            onChange={(e) => setBlockEntireDay(e.target.checked)}
            className="h-4 w-4 text-uzima-maroon-500 focus:ring-uzima-maroon-400 border-gray-300 rounded"
          />
          <label htmlFor="blockEntireDay" className="ml-2 block text-sm text-gray-700">
            Block Entire Day
          </label>
        </div>
        
        {/* Reason for Blocking Entire Day */}
        {blockEntireDay && (
          <div className="ml-6">
            <div className="flex items-center">
              <input
                type="radio"
                id="nonWorkingDay"
                name="blockingReason"
                value="non-working"
                checked={blockingReason === 'non-working'}
                onChange={(e) => setBlockingReason(e.target.value)}
                className="h-4 w-4 text-uzima-maroon-500 focus:ring-uzima-maroon-400 border-gray-300"
              />
              <label htmlFor="nonWorkingDay" className="ml-2 block text-sm text-gray-700">
                Non-working Day (e.g., Tuesday)
              </label>
            </div>
            <div className="flex items-center mt-2">
              <input
                type="radio"
                id="unavailableDay"
                name="blockingReason"
                value="unavailable"
                checked={blockingReason === 'unavailable'}
                onChange={(e) => setBlockingReason(e.target.value)}
                className="h-4 w-4 text-uzima-maroon-500 focus:ring-uzima-maroon-400 border-gray-300"
              />
              <label htmlFor="unavailableDay" className="ml-2 block text-sm text-gray-700">
                Unavailable (e.g., Sick, Leave)
              </label>
            </div>
            
            {/* Recurring Option (only for non-working days) */}
            {blockingReason === 'non-working' && (
              <div className="flex items-center mt-2 ml-4">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="h-4 w-4 text-uzima-maroon-500 focus:ring-uzima-maroon-400 border-gray-300 rounded"
                />
                <label htmlFor="isRecurring" className="ml-2 block text-sm text-gray-700">
                  Repeat weekly (every {getWeekdayName(selectedDate)})
                </label>
                <Repeat className="h-4 w-4 text-uzima-maroon-400 ml-1" />
              </div>
            )}
          </div>
        )}
        
        {/* Time Slot Selection (only if not blocking entire day) */}
        {!blockEntireDay && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Select Time Slot</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {TIME_SLOTS.map((slot) => {
                const isBlocked = isTimeSlotBlocked(slot);
                return (
                  <button
                    key={slot}
                    className={`p-2 rounded-md ${
                      selectedTimeSlot === slot
                        ? 'bg-uzima-maroon-500 text-white'
                        : isBlocked
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                    onClick={() => setSelectedTimeSlot(slot)}
                    disabled={isBlocked || isDayBlocked() || currentDayRecurringBlock}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
            {isDayBlocked() && (
              <p className="mt-2 text-sm text-yellow-600">
                <AlertCircle className="inline-block h-4 w-4 mr-1" />
                This entire day is already blocked.
              </p>
            )}
          </div>
        )}
        
        {/* Additional Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Additional Details (Optional)</label>
          <input
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-uzima-maroon-400 focus:ring-uzima-maroon-400"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder={blockEntireDay ? 
              (blockingReason === 'non-working' ? 
                (isRecurring ? "e.g., Counselor doesn't work on Tuesdays" : "e.g., Weekly off day") : 
                "e.g., Sick leave, Conference") : 
              "e.g., Emergency meeting, Personal leave"}
          />
        </div>
        
        {/* Submit Button */}
        <button
          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-400"
          onClick={handleBlockTimeSlot}
          disabled={loading || !selectedCounselor || !selectedDate || 
                  (!blockEntireDay && !selectedTimeSlot) || 
                  (isDayBlocked() && !blockEntireDay) ||
                  (isRecurring && isWeekdayInRecurringBlock()) ||
                  !!currentDayRecurringBlock}
        >
          {loading ? 'Blocking...' : 
           (blockEntireDay && isRecurring) ? `Block Every ${getWeekdayName(selectedDate)}` : 
           blockEntireDay ? 'Block Entire Day' : 
           'Block Time Slot'}
        </button>
      </div>
      
      {/* Current Blocked Information */}
      <div className="mt-8">
        <h3 className="text-lg font-medium mb-2">
          Currently Blocked for {getSelectedCounselorName()}
        </h3>
        
        {loadingBlocked ? (
          <div className="animate-pulse p-4 bg-gray-100 rounded">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ) : (
          <>
            {/* Recurring Weekly Blocks */}
            {recurringBlocks.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium mb-2 text-uzima-maroon-500">Weekly Non-Working Days</h4>
                <ul className="divide-y divide-gray-200">
                  {recurringBlocks.map((block) => {
                    // Convert weekday number to name
                    const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    const weekdayName = weekdayNames[block.weekDay];
                    
                    return (
                      <li key={block._id} className="py-3 flex justify-between items-center bg-uzima-maroon-50 px-4 rounded mb-2">
                        <div>
                          <div className="flex items-center font-medium">
                            <Repeat className="h-4 w-4 text-uzima-maroon-400 mr-1" />
                            <span>Every {weekdayName}</span>
                          </div>
                          {block.description && (
                            <p className="text-sm text-gray-600">{block.description}</p>
                          )}
                        </div>
                        <button
                          className="text-uzima-maroon-500 hover:text-blue-800"
                          onClick={() => handleUnblockTimeSlot(block._id, true)}
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            
            <h4 className="text-md font-medium mb-2">
              On {new Date(selectedDate).toLocaleDateString()}
            </h4>
            
            {/* Blocked Days */}
            {blockedDays.length > 0 && (
              <div className="mb-4">
                <h4 className="text-md font-medium mb-2 text-red-600">Entire Day Blocked</h4>
                <ul className="divide-y divide-gray-200">
                  {blockedDays.map((block) => (
                    <li key={block._id} className="py-3 flex justify-between items-center bg-red-50 px-4 rounded">
                      <div>
                        <div className="flex items-center font-medium">
                          <Calendar className="h-4 w-4 text-red-500 mr-1" />
                          <span>Entire Day</span>
                        </div>
                        {block.description && (
                          <p className="text-sm text-gray-600">{block.description}</p>
                        )}
                      </div>
                      <button
                        className="text-uzima-maroon-500 hover:text-blue-800"
                        onClick={() => handleUnblockTimeSlot(block._id)}
                      >
                        Unblock
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Blocked Slots (only show if day is not blocked) */}
            {blockedDays.length === 0 && !currentDayRecurringBlock && (
              <>
                {blockedSlots.length === 0 ? (
                  <p className="text-gray-500">No blocked slots for the selected date.</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {blockedSlots.map((block) => (
                      <li key={block._id} className="py-3 flex justify-between items-center">
                        <div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-gray-500 mr-1" />
                            <span>{block.startTime} - {block.endTime}</span>
                          </div>
                          {block.description && (
                            <p className="text-sm text-gray-500">{block.description}</p>
                          )}
                        </div>
                        <button
                          className="text-uzima-maroon-500 hover:text-blue-800"
                          onClick={() => handleUnblockTimeSlot(block._id)}
                        >
                          Unblock
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlockCounselorTimeSlot;